import { canonicalDayChoice } from './days.js';
import { canonicalIslamicFixes, defaultIslamicTradition, isIslamicTradition, } from './islamic.js';
import { kitchensWithSuggestion } from './traditions.js';
import { canonical, FAITHS, FOOD_RULES, isMadhab, KITCHENS, PRACTICES, } from './vocab.js';
export const EMPTY_HOUSEHOLD_PROFILE = Object.freeze({
    kitchens: [],
    faiths: null,
    foodRules: [],
    daySets: [],
    dayKeysOff: [],
    icsDays: false,
    suggestionAnswered: false,
});
/** Niets ingesteld: alles zichtbaar wat er al was, geen eetregels, standaardvolgorde. */
export const UNSET_PROFILE = Object.freeze({
    isSet: false,
    faiths: null,
    foodRules: [],
    kitchens: [],
    source: Object.freeze({ faiths: 'unset', foodRules: 'unset' }),
});
/**
 * De vorm die de server opslaat (builder A roept dit aan vóór elke PUT): canonieke codes, geen
 * uitgezette dag of ICS zonder aangezette set, en `suggestionAnswered` alleen zolang er een keuken
 * met een voorstel aan staat. Leidt NIETS af: geloof en eetregels blijven precies wat de ouder koos.
 */
export function normalizeHouseholdProfile(p) {
    const days = canonicalDayChoice({ daySets: p.daySets, dayKeysOff: p.dayKeysOff });
    const kitchens = canonical(p.kitchens, KITCHENS);
    const withCard = kitchensWithSuggestion();
    // Traditie en bevestigingen alleen met de islamitische set aan (minimalisatie, 0030 CHECK).
    const islamicOn = days.daySets.indexOf('islamic') >= 0;
    const tradition = islamicOn && p.islamicTradition && isIslamicTradition(p.islamicTradition)
        ? p.islamicTradition
        : null;
    const effectiveTradition = tradition ?? defaultIslamicTradition();
    const faiths = p.faiths === null ? null : canonical(p.faiths, FAITHS);
    return {
        kitchens,
        faiths: p.faiths === null ? null : canonical(p.faiths, FAITHS),
        foodRules: canonical(p.foodRules, FOOD_RULES),
        daySets: days.daySets,
        dayKeysOff: days.dayKeysOff,
        icsDays: p.icsDays && days.daySets.length > 0,
        suggestionAnswered: p.suggestionAnswered && kitchens.some((k) => withCard.indexOf(k) >= 0),
        // Gewoontes alleen bij een gekozen geloof, en alleen die van dat geloof (minimalisatie). Niet
        // gevraagd (null) = het veld blijft weg.
        ...practicesField(normalizePractices(p.practices ?? null, faiths)),
        // Golf 6c: de mazhab alleen bij Islam in het gezinsprofiel (anders valt hij weg: minimalisatie,
        // 0045 CHECK). Weg = "weet ik niet".
        ...madhabField(normalizeMadhab(p.madhab ?? null, faiths)),
        // Alleen met de islamitische set aan (anders blijven de velden weg: niets overbodigs).
        ...(islamicOn
            ? {
                islamicTradition: tradition,
                islamicFixes: canonicalIslamicFixes(p.islamicFixes, effectiveTradition),
            }
            : {}),
    };
}
/** Gewoontes per geloof (stap 1 van het profiel toont ze als vinkjes na het kiezen van een geloof). */
export const FAITH_PRACTICE_CODES = {
    islam: ['prayer', 'wudu', 'mosque', 'quran'],
    christianity: ['prayer', 'church'],
    judaism: ['prayer', 'shabbat', 'synagogue'],
    hinduism: ['prayer', 'mandir'],
    other: ['prayer'],
};
/** Alle gewoontes die bij deze geloven horen (volgorde van PRACTICES). */
export function practicesOfFaiths(faiths) {
    if (!faiths)
        return [];
    const all = [];
    for (const f of faiths)
        all.push(...(FAITH_PRACTICE_CODES[f] ?? []));
    return canonical(all, PRACTICES);
}
/**
 * Canonieke gewoontes: null blijft null (niet gevraagd); anders alleen bekende codes die bij het
 * gekozen geloof horen. Zonder geloof (null of "Geen geloof") is er niets te kiezen: null.
 */
export function normalizePractices(practices, faiths) {
    if (practices === null || !faiths || faiths.length === 0)
        return null;
    const allowed = practicesOfFaiths(faiths);
    return canonical(practices, PRACTICES).filter((p) => allowed.indexOf(p) >= 0);
}
/**
 * Canonieke mazhab: alleen een bekende code, en alleen als het gezin Islam koos. Anders null (de
 * vraag hoort dan niet bij dit gezin; een oude keuze telt niet meer).
 */
export function normalizeMadhab(madhab, faiths) {
    if (!faiths || faiths.indexOf('islam') < 0)
        return null;
    return isMadhab(madhab) ? madhab : null;
}
/** `{ madhab }` alleen als er een gekozen is (anders blijft het veld weg). */
function madhabField(madhab) {
    return madhab === null ? {} : { madhab };
}
/** `{ practices }` alleen als er iets gevraagd is (anders blijft het veld weg). */
function practicesField(practices) {
    return practices === null ? {} : { practices };
}
/** Heeft het huishouden iets ingesteld? Anders: de rustige uitnodigingskaart tonen. */
export function profileIsSet(household) {
    return (household !== null &&
        (household.kitchens.length > 0 ||
            household.faiths !== null ||
            household.foodRules.length > 0 ||
            household.daySets.length > 0));
}
/** Heeft een afwijking inhoud? Een lege afwijking hoort geen rij te zijn (0023 CHECK). */
export function overrideHasContent(o) {
    return !!o && (o.faiths !== null || o.foodRulesExtra.length > 0 || o.foodRulesSkip.length > 0);
}
/** Eetregels van een lid: (gezin ∪ extra) \ uitgezonderd. */
export function memberFoodRules(family, override) {
    if (!override)
        return canonical(family, FOOD_RULES);
    const all = canonical([...family, ...override.foodRulesExtra], FOOD_RULES);
    return all.filter((r) => override.foodRulesSkip.indexOf(r) < 0);
}
/**
 * Van wat de ouder in het blad van een lid aanvinkt (`chosen`, de volledige lijst) naar de delta
 * die we bewaren. De UI toont de gezinskeuze voorgevinkt; wat er dan staat, geldt nu, en latere
 * gezinswijzigingen stromen door (behalve wat voor dit lid expliciet uit staat).
 */
export function foodDelta(family, chosen) {
    const fam = canonical(family, FOOD_RULES);
    const ch = canonical(chosen, FOOD_RULES);
    return {
        foodRulesExtra: ch.filter((r) => fam.indexOf(r) < 0),
        foodRulesSkip: fam.filter((r) => ch.indexOf(r) < 0),
    };
}
/**
 * Effectief profiel van één lid (of van het huishouden zelf met `member = null`). Geloof: de
 * afwijking vervangt; eten: delta op het gezin (memberFoodRules).
 */
export function effectiveProfile(household, member = null) {
    const memberFaiths = member ? member.faiths : null;
    const hhFaiths = household ? household.faiths : null;
    const hasFoodDelta = !!member && (member.foodRulesExtra.length > 0 || member.foodRulesSkip.length > 0);
    const faithSource = memberFaiths !== null ? 'member' : hhFaiths !== null ? 'household' : 'unset';
    const ruleSource = hasFoodDelta ? 'member' : household ? 'household' : 'unset';
    return {
        isSet: profileIsSet(household) || overrideHasContent(member),
        faiths: memberFaiths !== null
            ? canonical(memberFaiths, FAITHS)
            : hhFaiths !== null
                ? canonical(hhFaiths, FAITHS)
                : null,
        foodRules: memberFoodRules(household?.foodRules ?? [], member),
        kitchens: canonical(household?.kitchens ?? [], KITCHENS),
        source: { faiths: faithSource, foodRules: ruleSource },
        ...practicesField(memberFaiths === null && household
            ? normalizePractices(household.practices ?? null, hhFaiths)
            : null),
    };
}
/** De dagkeuze van het huishouden (canoniek; zonder profiel: geen extra dagen). */
export function dayChoiceOf(household) {
    if (!household)
        return { daySets: [], dayKeysOff: [] };
    return canonicalDayChoice({
        daySets: household.daySets,
        dayKeysOff: household.dayKeysOff,
        islamic: {
            tradition: effectiveIslamicTradition(household),
            fixes: household.islamicFixes ?? {},
        },
    });
}
/**
 * De traditie die telt: de keuze van het gezin, anders de vaste standaard (nooit uit de keuken;
 * review golf 6c r1).
 */
export function effectiveIslamicTradition(household) {
    const t = household?.islamicTradition;
    if (t && isIslamicTradition(t))
        return t;
    return defaultIslamicTradition();
}
export function householdView(household, members) {
    return {
        household: effectiveProfile(household),
        people: members.map((m) => ({
            memberId: m.memberId,
            name: m.name,
            profile: effectiveProfile(household, m.override),
        })),
    };
}
/**
 * Geloof van een groep (bijvoorbeeld de kinderen die op de kiosk samen een lijst zien): de
 * VERENIGING; is één lid onbekend (null), dan onbekend (alles tonen). Iets verbergen voor een groep
 * mag alleen als het voor niemand in die groep past.
 */
export function unionFaiths(profiles) {
    if (profiles.length === 0)
        return null;
    const out = [];
    for (const p of profiles) {
        if (p.faiths === null)
            return null;
        out.push(...p.faiths);
    }
    return canonical(out, FAITHS);
}
//# sourceMappingURL=effective.js.map